import { List } from 'immutable';
import { Record } from '@saucelabs/sl-util-immutable';

const EspressoClassClass = Record({
  active: false,
  name: '',
  tests: new List(),
  id: 0,
});

const EspressoTestClass = Record({
  class: '',
  failure_reason: null,
  name: '',
  // For native frameworks, 'SUCCESS' status indicates a passed test,
  // while 'FAILURE' status indicates a failed test.
  status: '',
  // 0 means passed, 1 means failed.
  status_code: 0,
  stream: null,
  test_number: 0,
  start_time: null,
  duration: '',
});

const FailureRecord = Record({
  content: '',
  message: '',
  type: '',
});

class Failure extends FailureRecord {}

const AttachmentRecord = Record({
  name: '',
  contentType: '',
  path: '',
});

class Attachment extends AttachmentRecord {}

/**
* A TestCase record, maps to <testcase /> elements in a JUnit report.
*
* @property {List<Attachment>} attachments - Assets that are associated with the test case.
* @property {string} classname - Full class name for the class the test method is in.
* @property {string} key - Uniquely identify the testcase. N.B. this is a synthetic property
* and is not deserialized from the JUnit report.
* @property {string} name - TestCase name. Usually corresponds to a framework's
* `it()` or `test()` statement.
* @property {string | null} time - Time taken (in seconds) to execute the test.
* @property {Failure | null} failure - If the testcase failed, contains failure information.
* @property {string} status - The status of the test
* @property {number} status_code - The status code of the test
* @property {string | null} stream - The test stream
* @property {number} test_number - The test count
* @property {number | null} video_timestamp - The timestamp in the associated video that
* corresponds to the start of this TestCase.
*/
const TestCaseRecord = Record({
  attachments: List(),
  classname: '',
  key: '',
  name: '',
  time: '',
  failure: null,
  status: '',
  status_code: 0,
  stream: null,
  test_number: 0,
  video_timestamp: null,
});

/**
* A generic TestCaseRecord class.
* {@link TestCaseRecord}
*/
class TestCase extends TestCaseRecord {
  /**
   * returns start time of the test case in seconds
   * @returns {number}
   */
  getTimeline() {
    return this.video_timestamp;
  }
}

/**
* A Property record, maps to <property /> elements in a JUnit report.
*
* @property {string} name - The name of the property.
* @property {string} value - The value of the property.
*/
const PropertyRecord = Record({
  name: '',
  value: '',
});

class Property extends PropertyRecord {}

/**
* Testsuite record, maps to a JUnit <testsuite/> element. Record properties map to the attributes of
* the <testsuite /> element.
*
* @property {number} id - Uniquely identify the testsuite. Starts at 0 for the first testsuite
* and is incremented by 1 for each following testsuite.
* @property {number} failures - Number of test cases that failed.
* @property {string} name - The name of the testsuite.
* @property {number} tests - Number of tests executed.
* @property {string} time - Execution time of the testsuite (in seconds).
* @property {string} timestamp - ISO 8601 date when tests were executed.
* @property {Immutable.List<TestCase>} testcases - A List of TestCases.
* @property {Immutable.List<Property>} properties - A List of Properties.
*/
const TestsuiteRecord = Record({
  id: 0,
  failures: 0,
  name: '',
  tests: 0,
  timestamp: '',
  time: '',
  testcases: new List(),
  properties: new List(),
});

class Testsuite extends TestsuiteRecord {}

/**
* TestResult record, encapsulates a junit report from a devx batch job.
*
* @property {number} disabled - number of test cases that are disabled.
* @property {number} errors - number of test cases that errored.
* @property {number} failures - number of failed test cases.
* @property {string} name - saucectl suite name.
* @property {string} time - execution time in seconds.
* @property {number} tests - number of test cases executed.
* @property {Immutable.List<Testsuite>} testsuites - a List of Testsuites that were executed.
*/
const TestResultRecord = Record({
  disabled: 0,
  errors: 0,
  failures: 0,
  name: '',
  time: '',
  tests: 0,
  testsuites: new List(),
});

class TestResult extends TestResultRecord {}

/**
  * More generic Espresso test record class (not sure why we have this)
  * @property {boolean} active
  * @property {string} name
  * @property {Immutable.List<*>} tests
  * @property {number} id
  */
class EspressoClass extends EspressoClassClass {}

/**
  * Espresso test record class
  * @property {string} class
  * @property {*} failure_reason
  * @property {string} name
  * @property {string} status
  * @property {number} status_code
  * @property {*} stream
  * @property {number} test_number
  */
class EspressoTest extends EspressoTestClass {
  /**
   * Determines whether the test was successful.
   * @returns {boolean}
   */
  isSuccess() {
    return this.status_code === 0;
  }

  /**
   * Determines whether the test failed
   * @returns {boolean}
   */
  isFailed() {
    return this.status_code !== 0;
  }
}

export {
  EspressoClass,
  EspressoTest,
  EspressoClassClass,
  EspressoTestClass,

  Attachment,
  TestResult,
  Testsuite,
  TestCase,
  Property,
  Failure,
};

export default TestCase;
